import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  School,
  Close,
} from "@mui/icons-material";

import HttpClient from "../../../../helpers/HttpClient";
import OverlayLoader from "../../../../components/OverlayLoader";
import { showToast } from "../../../../utils/toast";
import { useAdminModulePermission } from "../../../../contexts/AdminPermissionContext";

// Data types
interface Usuario {
  id: string;
  nombre_completo: string;
  email: string;
  nickname: string;
  estado: "activo" | "inactivo";
}

interface Curso {
  activacion_id: number;
  pin: string;
  code: string;
  name: string;
  chatEnabled: boolean;
  licenciaExpirada?: boolean;
  pinHabilitado?: boolean;
}

interface UsuarioCurso extends Usuario {
  cursosActivos: Curso[];
}

export default function SearchUserCoursesPage() {
  const { isReadOnly } = useAdminModulePermission();
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UsuarioCurso | null>(null);
  const [selectedCourses, setSelectedCourses] = useState<Curso[]>([]);
  

  const [confirmChatDialogOpen, setConfirmChatDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [pendingCourse, setPendingCourse] = useState<Curso | null>(null);
  const [pendingChatState, setPendingChatState] = useState(false);
  

  const [massActionDialogOpen, setMassActionDialogOpen] = useState(false);
  const [massActionType, setMassActionType] = useState<"enable" | "disable">("enable");
  const [pendingCoursesForMassAction, setPendingCoursesForMassAction] = useState<Curso[]>([]);


  const handleSearch = async (value: string) => {
    setSearchInput(value);

    if (!value.trim()) {
      setSearchResults([]);
      setSelectedUser(null);
      setSelectedCourses([]);
      return;
    }

    setLoading(true);
    try {
      const response: any = await HttpClient().get(`/admin/user-data/${value.trim()}`);

      if (response.error === true) {
        setSearchResults([]);
        setSelectedUser(null);
        setSelectedCourses([]);
        showToast(response.data?.error?.message || "Usuario no encontrado", "error");
      } else if (response.data) {
        const userData: Usuario = {
          id: response.data.id || "",
          nombre_completo: response.data.nombre_completo || "",
          email: response.data.email || "",
          nickname: response.data.nickname || "",
          estado: response.data.activo === 1 ? "activo" : "inactivo",
        };


        const cursos: Curso[] = Array.isArray(response.data.cursos) ? response.data.cursos : [];

        setSearchResults([userData]);
        setSelectedUser({
          ...userData,
          cursosActivos: cursos,
        });
        setSelectedCourses(cursos);
        showToast("Usuario encontrado", "success");
      } else {
        setSearchResults([]);
        setSelectedUser(null);
        setSelectedCourses([]);
        showToast("No se pudo obtener la información del usuario", "error");
      }
    } catch (error: any) {
      console.error(error);
      setSearchResults([]);
      setSelectedUser(null);
      setSelectedCourses([]);
      showToast(error.message || "Error al buscar el usuario", "error");
    } finally {
      setLoading(false);
    }
  };


  const handleSelectUser = (user: Usuario, cursos: Curso[] = []) => {
    setSelectedUser({
      ...user,
      cursosActivos: cursos,
    });
    setSelectedCourses(cursos);
  };

  const handleSaveCourses = () => {
    if (selectedUser) {
      showToast(
        `Cursos del usuario ${selectedUser.nombre_completo} guardados correctamente`,
        "success"
      );
    }
  };


  const updateCourseChat = async (
    coursesToUpdate: Curso[],
    chatState: boolean,
    actionType: "individual" | "mass"
  ) => {
    if (coursesToUpdate.length === 0) return;

    setLoading(true);
    try {
      const cursoData = coursesToUpdate.map((course) => ({
        activacion_id: course.activacion_id,
        cod_curso: course.code,
        chat_enabled: chatState,
      }));

      const response: any = await HttpClient().put(`/admin/course-chat`, {
        cursos: cursoData,
      });

      if (response.error === true) {
        showToast(response.data?.error?.message || "Error al actualizar los cursos", "error");
      } else {

        setSelectedCourses(
          selectedCourses.map((course) =>
            coursesToUpdate.some((c) => c.code === course.code)
              ? { ...course, chatEnabled: chatState }
              : course
          )
        );


        if (actionType === "mass") {
          showToast("Cursos actualizados correctamente", "success");
        } else {
          showToast("Chat actualizado correctamente", "success");
        }
      }
    } catch (error: any) {
      console.error(error);
      showToast(error.message || "Error al actualizar los cursos", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteCourse = async (course: Curso) => {
    setLoading(true);
    try {
      const response: any = await HttpClient().delete(
        `/admin/course-activation/${course.activacion_id}/${course.code}`
      );

      if (response.error === true) {
        showToast(response.data?.error?.message || "Error al eliminar el curso", "error");
      } else {

        setSelectedCourses(
          selectedCourses.filter((c) => c.code !== course.code)
        );
        showToast("Curso eliminado correctamente", "success");
      }
    } catch (error: any) {
      console.error(error);
      showToast(error.message || "Error al eliminar el curso", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChatAll = (enable: boolean) => {
    setPendingCoursesForMassAction(selectedCourses);
    setMassActionType(enable ? "enable" : "disable");
    setMassActionDialogOpen(true);
  };

  const handleConfirmMassAction = async () => {
    if (pendingCoursesForMassAction.length === 0) return;

    await updateCourseChat(
      pendingCoursesForMassAction,
      massActionType === "enable",
      "mass"
    );

    setMassActionDialogOpen(false);
    setPendingCoursesForMassAction([]);
  };

  const handleToggleChat = (courseCode: string) => {
    const course = selectedCourses.find((c) => c.code === courseCode);
    if (course) {
      setPendingCourse(course);
      setPendingChatState(!course.chatEnabled);
      setConfirmChatDialogOpen(true);
    }
  };

  const handleConfirmChatChange = async () => {
    if (!pendingCourse) return;

    await updateCourseChat([pendingCourse], pendingChatState, "individual");

    setConfirmChatDialogOpen(false);
    setPendingCourse(null);
  };

  const handleRemoveCourse = (courseCode: string) => {
    const course = selectedCourses.find((c) => c.code === courseCode);
    if (course) {
      setPendingCourse(course);
      setConfirmDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingCourse) return;

    await deleteCourse(pendingCourse);

    setConfirmDeleteDialogOpen(false);
    setPendingCourse(null);
  };

  const getEstadoChip = (estado: string) => {
    return (
      <Chip
        label={estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase()}
        color={estado === "activo" ? "success" : "default"}
        size="small"
        variant="outlined"
      />
    );
  };


  useEffect(() => {


  }, []);

  return (
    <>
      <OverlayLoader open={loading} ready={false} buttonClick={() => {}} />
      
      <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            color="#374151"
            sx={{ fontSize: "1.3rem" }}
          >
            Buscar Cursos de Usuarios
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "white",
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Search sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ fontSize: "0.85rem" }}
                  >
                    Buscar por Correo o Nombre de Usuario:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="usuario@ejemplo.com o usuario123"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearch(searchInput);
                    }
                  }}
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", height: "100%" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Search />}
                    onClick={() => handleSearch(searchInput)}
                    disabled={loading}
                    size="small"
                    sx={{ height: "40px" }}
                  >
                    Buscar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>



      {/* User Info Card */}
      {selectedUser && (
        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "white",
            mt: 3,
            mb: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#6B7280", fontSize: "0.75rem", mb: 0.5 }}
                  >
                    NOMBRE COMPLETO
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#374151", fontSize: "1rem" }}
                  >
                    {selectedUser.nombre_completo}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#6B7280", fontSize: "0.75rem", mb: 0.5 }}
                  >
                    CORREO
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "#374151", fontSize: "1rem" }}
                  >
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#6B7280", fontSize: "0.75rem", mb: 0.5 }}
                  >
                    USUARIO
                  </Typography>
                  <Chip
                    label={selectedUser.nickname}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: "#6B7280", fontSize: "0.75rem", mb: 0.5 }}
                  >
                    ESTADO
                  </Typography>
                  {getEstadoChip(selectedUser.estado)}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Courses Card Display */}
      {selectedUser && (
        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "white",
            mt: 0,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Title */}
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ mb: 2, color: "#374151", fontSize: "1rem" }}
            >
              Curso(s) Activos: ({selectedCourses.length})
            </Typography>

            {/* Selected Courses Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ mb: 2, color: "#374151", fontSize: "0.95rem" }}
              >
                Cursos Seleccionados:
              </Typography>

              {/* Toggle All Buttons */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                {!isReadOnly && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleToggleChatAll(false)}
                      sx={{
                        borderColor: "#10B981",
                        color: "#10B981",
                        fontSize: "0.75rem",
                        height: "36px",
                        textTransform: "none",
                      }}
                    >
                      DESACTIVAR CHAT TODOS
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleToggleChatAll(true)}
                      sx={{
                        backgroundColor: "#10B981",
                        color: "white",
                        fontSize: "0.75rem",
                        height: "36px",
                        textTransform: "none",
                      }}
                    >
                      ACTIVAR CHAT TODOS
                    </Button>
                  </>
                )}
              </Box>

              {/* Selected Courses List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {selectedCourses.length > 0 ? (
                  selectedCourses.map((course) => (
                    <Box
                      key={course.code}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        p: 1.5,
                        backgroundColor: "#F9FAFB",
                        border: "1px solid #E5E7EB",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={`${course.name}`}
                            color="success"
                            size="small"
                            icon={<School />}
                          />
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#6B7280" }}>
                            ({course.code})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Box>
                            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#6B7280", fontWeight: "bold" }}>
                              PIN:
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "#374151", ml: 0.5 }}>
                              {course.pin}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#6B7280", fontWeight: "bold" }}>
                              Licencia:
                            </Typography>
                            <Chip
                              label={course.licenciaExpirada ? "Expirada" : "Vigente"}
                              size="small"
                              sx={{
                                ml: 0.5,
                                height: "18px",
                                fontSize: "0.65rem",
                                backgroundColor: course.licenciaExpirada ? "#FEE2E2" : "#DCFCE7",
                                color: course.licenciaExpirada ? "#991B1B" : "#166534",
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "#6B7280", fontWeight: "bold" }}>
                              PIN Status:
                            </Typography>
                            <Chip
                              label={course.pinHabilitado ? "Habilitado" : "Deshabilitado"}
                              size="small"
                              sx={{
                                ml: 0.5,
                                height: "18px",
                                fontSize: "0.65rem",
                                backgroundColor: course.pinHabilitado ? "#DBEAFE" : "#FEF3C7",
                                color: course.pinHabilitado ? "#1E40AF" : "#92400E",
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, ml: 1 }}>
                        <Switch
                          checked={course.chatEnabled || false}
                          onChange={() => handleToggleChat(course.code)}
                          size="small"
                          color="success"
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => handleRemoveCourse(course.code)}
                            sx={{ color: "#EF4444", minWidth: "auto" }}
                          >
                            <Close sx={{ fontSize: 18 }} />
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography color="#6B7280" variant="body2">
                    No hay cursos seleccionados
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="text"
                onClick={() => setSelectedUser(null)}
                sx={{
                  color: "#10B981",
                  textTransform: "none",
                  fontSize: "0.85rem",
                }}
              >
                CANCELAR
              </Button>
              {!isReadOnly && (
                <Button
                  variant="contained"
                  onClick={handleSaveCourses}
                  sx={{
                    backgroundColor: "#10B981",
                    color: "white",
                    textTransform: "none",
                    fontSize: "0.85rem",
                    height: "36px",
                  }}
                >
                  GUARDAR CAMBIOS
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Diálogo de confirmación para cambio de chat */}
      <Dialog open={confirmChatDialogOpen} onClose={() => setConfirmChatDialogOpen(false)}>
        <DialogTitle>Confirmar cambio de chat</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            ¿Deseas {pendingChatState ? "habilitar" : "deshabilitar"} el chat para el curso{" "}
            <strong>{pendingCourse?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmChatDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmChatChange} variant="contained" color="success">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para acciones masivas */}
      <Dialog open={massActionDialogOpen} onClose={() => setMassActionDialogOpen(false)}>
        <DialogTitle>Confirmar acción masiva</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            ¿Deseas {massActionType === "enable" ? "habilitar" : "deshabilitar"} el chat para{" "}
            <strong>{pendingCoursesForMassAction.length} curso(s)</strong>?
          </Typography>
          {pendingCoursesForMassAction.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: "bold", color: "#6B7280" }}>
                Cursos afectados:
              </Typography>
              <Box sx={{ mt: 1, maxHeight: "150px", overflowY: "auto" }}>
                {pendingCoursesForMassAction.map((course) => (
                  <Typography key={course.code} variant="caption" display="block" sx={{ color: "#374151" }}>
                    • {course.name} ({course.code})
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMassActionDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmMassAction} variant="contained" color="success">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para eliminar curso */}
      <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            ¿Deseas eliminar el curso <strong>{pendingCourse?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
}
